// src/pages/Dashboard/Admin/SeasonStats/components/SeasonStatsEditorDrawer.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaPlus,
  FaSave,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

import {
  createDefaultBreakdowns,
  createEmptyBreakdown,
  getSourceLabel,
  prepareBreakdownsForEditor,
  toStatCode,
} from "../utils/seasonStatsDashboardUtils";

const initialForm = {
  season: "",
  snapshot_date: "",
  notes: "",
};

const SECTIONS = [
  {
    type: "TOTAL",
    title: "Indicateurs généraux",
    description:
      "Effectif total ou autres indicateurs globaux.",
  },
  {
    type: "GENDER",
    title: "Genre",
    description:
      "Répartition garçons / filles.",
  },
  {
    type: "AGE_RANGE",
    title: "Tranches d’âge",
    description:
      "Répartition des adhérents selon leur âge.",
  },
  {
    type: "GROUP",
    title: "Groupes",
    description:
      "Effectifs et capacité éventuelle des groupes.",
  },
  {
    type: "DISCIPLINE",
    title: "Disciplines",
    description:
      "Judo, Taïso ou autres disciplines du club.",
  },
];

export default function SeasonStatsEditorDrawer({
  isOpen,
  onClose,
  seasonStat,
  saving,
  onCreate,
  onUpdate,
}) {
  const isEdit = Boolean(seasonStat?.id);

  const [formData, setFormData] =
    useState(initialForm);

  const [breakdowns, setBreakdowns] =
    useState([]);

  useEffect(() => {
    if (!isOpen) return;

    if (seasonStat) {
      setFormData({
        season: seasonStat.season || "",

        snapshot_date:
          seasonStat.snapshot_date
            ? String(
                seasonStat.snapshot_date,
              ).slice(0, 10)
            : "",

        notes: seasonStat.notes || "",
      });

      setBreakdowns(
        prepareBreakdownsForEditor(
          seasonStat.breakdowns || [],
        ),
      );
    } else {
      setFormData({
        ...initialForm,
        snapshot_date: new Date()
          .toISOString()
          .slice(0, 10),
      });

      setBreakdowns(
        createDefaultBreakdowns(),
      );
    }
  }, [isOpen, seasonStat]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow =
      root.style.overflow;

    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (
        event.key === "Escape" &&
        !saving
      ) {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      onEscape,
    );

    return () => {
      root.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        onEscape,
      );
    };
  }, [isOpen, saving, onClose]);

  const canSubmit = useMemo(() => {
    if (
      !String(formData.season || "").trim()
    ) {
      return false;
    }

    if (saving) return false;

    return breakdowns.every((item) => {
      const code = String(
        item.code || "",
      ).trim();

      const label = String(
        item.label || "",
      ).trim();

      return code && label;
    });
  }, [formData.season, breakdowns, saving]);

  const updateForm = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const updateBreakdown = (
    clientId,
    name,
    value,
  ) => {
    setBreakdowns((prev) =>
      prev.map((item) => {
        if (
          item.client_id !== clientId
        ) {
          return item;
        }

        const next = {
          ...item,
          [name]: value,
        };

        if (
          name === "label" &&
          !String(item.code || "").trim()
        ) {
          const normalized =
            toStatCode(value);

          if (
            item.breakdown_type ===
            "GROUP"
          ) {
            next.code = normalized
              ? `GROUP_${normalized}`
              : "";
          } else if (
            item.breakdown_type ===
            "DISCIPLINE"
          ) {
            next.code = normalized;
          }
        }

        return next;
      }),
    );
  };

  const addRow = (type) => {
    setBreakdowns((prev) => [
      ...prev,
      createEmptyBreakdown(type),
    ]);
  };

  const deleteRow = (clientId) => {
    setBreakdowns((prev) =>
      prev.filter(
        (item) =>
          item.client_id !== clientId,
      ),
    );
  };

  const handleSubmit = async (
    event,
  ) => {
    event.preventDefault();

    if (!canSubmit) return;

    if (isEdit) {
      await onUpdate?.(
        seasonStat.id,
        formData,
        breakdowns,
      );

      return;
    }

    await onCreate?.(
      formData,
      breakdowns,
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-4xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              {isEdit
                ? "Modification"
                : "Nouvelle saison"}
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {isEdit
                ? `Statistiques ${seasonStat?.season || ""}`
                : "Saisir des statistiques"}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {isEdit
                ? `Source actuelle : ${getSourceLabel(
                    seasonStat?.source,
                  )}. Toute correction d'un snapshot automatique le fera passer en mode mixte.`
                : "Renseignez les chiffres connus. Vous pouvez ajouter autant de groupes ou disciplines que nécessaire."}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-4 sm:p-5"
        >
          <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
            <h3 className="font-bold text-slate-900 dark:text-white">
              Informations de la saison
            </h3>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Saison
                </label>

                <input
                  type="text"
                  value={formData.season}
                  onChange={(event) =>
                    updateForm(
                      "season",
                      event.target.value,
                    )
                  }
                  placeholder="2025-2026"
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Date du snapshot
                </label>

                <input
                  type="date"
                  value={
                    formData.snapshot_date
                  }
                  onChange={(event) =>
                    updateForm(
                      "snapshot_date",
                      event.target.value,
                    )
                  }
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Notes
              </label>

              <textarea
                value={formData.notes}
                onChange={(event) =>
                  updateForm(
                    "notes",
                    event.target.value,
                  )
                }
                rows={3}
                placeholder="Informations complémentaires..."
                className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />
            </div>
          </section>

          <div className="mt-5 space-y-5">
            {SECTIONS.map((section) => {
              const rows =
                breakdowns.filter(
                  (item) =>
                    item.breakdown_type ===
                    section.type,
                );

              return (
                <section
                  key={section.type}
                  className="rounded-3xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white">
                        {section.title}
                      </h3>

                      <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                        {
                          section.description
                        }
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        addRow(section.type)
                      }
                      className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    >
                      <FaPlus />
                      Ajouter
                    </button>
                  </div>

                  <div className="mt-4 space-y-3">
                    {rows.length === 0 ? (
                      <p className="rounded-2xl bg-slate-50 p-4 text-center text-xs text-slate-500 dark:bg-slate-800/40">
                        Aucune donnée.
                      </p>
                    ) : (
                      rows.map((item) => (
                        <div
                          key={
                            item.client_id
                          }
                          className="grid grid-cols-1 gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-800/40 sm:grid-cols-[1fr,1fr,100px,auto]"
                        >
                          <div>
                            <label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
                              Libellé
                            </label>

                            <input
                              type="text"
                              value={
                                item.label || ""
                              }
                              onChange={(
                                event,
                              ) =>
                                updateBreakdown(
                                  item.client_id,
                                  "label",
                                  event.target
                                    .value,
                                )
                              }
                              className="w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-sm font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                            />
                          </div>

                          <div>
                            <label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
                              Code
                            </label>

                            <input
                              type="text"
                              value={
                                item.code || ""
                              }
                              onChange={(
                                event,
                              ) =>
                                updateBreakdown(
                                  item.client_id,
                                  "code",
                                  event.target
                                    .value,
                                )
                              }
                              className="w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-xs font-bold uppercase text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                            />
                          </div>

                          <div>
                            <label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
                              Effectif
                            </label>

                            <input
                              type="number"
                              min="0"
                              value={
                                item.value ?? 0
                              }
                              onChange={(
                                event,
                              ) =>
                                updateBreakdown(
                                  item.client_id,
                                  "value",
                                  event.target
                                    .value,
                                )
                              }
                              className="w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                            />
                          </div>

                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={() =>
                                deleteRow(
                                  item.client_id,
                                )
                              }
                              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                            >
                              <FaTrash />
                            </button>
                          </div>

                          {section.type ===
                          "GROUP" ? (
                            <div className="sm:col-span-2">
                              <label className="mb-1 block text-[10px] font-bold uppercase text-slate-500">
                                Capacité /
                                quota
                              </label>

                              <input
                                type="number"
                                min="0"
                                value={
                                  item.capacity ??
                                  ""
                                }
                                onChange={(
                                  event,
                                ) =>
                                  updateBreakdown(
                                    item.client_id,
                                    "capacity",
                                    event.target
                                      .value,
                                  )
                                }
                                placeholder="Facultatif"
                                className="w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                              />
                            </div>
                          ) : null}
                        </div>
                      ))
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />

              {saving
                ? "Enregistrement..."
                : isEdit
                  ? "Enregistrer les modifications"
                  : "Créer la saison"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}